import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Empty, Form, Input, Popconfirm, Select, Space, Switch, Table } from 'antd';
import { Users, Save, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './AdminUsers.css';

const roleOptions = [
  { value: 'student', label: 'Student' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'org_admin', label: 'Org Admin' },
  { value: 'platform_admin', label: 'Platform Admin' }
];

const orgAdminRoleOptions = [
  { value: 'student', label: 'Student' },
  { value: 'teacher', label: 'Teacher' }
];

const AdminUsers = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'platform_admin';
  const isPlatformAdmin = user?.role === 'platform_admin';
  const canEditAdminRoles = isPlatformAdmin;
  const canChangeOrg = isPlatformAdmin;
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  const orgNameById = useMemo(() => {
    const map = new Map();
    (organizations || []).forEach((org) => {
      map.set(org.id, org.name);
    });
    return map;
  }, [organizations]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/organizations', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setOrganizations(response.data || []);
    } catch (error) {
      // Non-blocking: user list can still load.
      console.error('Failed to load organizations', error);
    }
  }, []);

  const fetchUsers = useCallback(async (searchTerm = '') => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.get('/api/admin/users', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        params: searchTerm ? { q: searchTerm } : undefined
      });
      setUsers(response.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to load users.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      if (isPlatformAdmin) {
        fetchOrganizations();
      }
      fetchUsers();
    }
  }, [fetchOrganizations, fetchUsers, isAdmin, isPlatformAdmin]);

  const handleUserChange = (id, field, value) => {
    setUsers((prev) => prev.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  };

  const saveUser = async (userRow) => {
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        role: userRow.role,
        is_active: userRow.is_active,
        is_verified: userRow.is_verified,
      };
      if (canChangeOrg) {
        payload.organization_id = userRow.organization_id;
      }

      await axios.put(`/api/admin/users/${userRow.id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setMessage({ type: 'success', text: `Saved user ${userRow.email || userRow.username}` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update user.' });
    }
  };

  const deactivateUser = async (userRow) => {
    setMessage({ type: '', text: '' });
    const label = userRow.email || userRow.username || `User ${userRow.id}`;

    try {
      const response = await axios.delete(`/api/admin/users/${userRow.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      const updated = response.data;
      setUsers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setMessage({ type: 'success', text: `Deactivated ${label}` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to deactivate user.' });
    }
  };

  const hardDeleteUser = async (userRow) => {
    setMessage({ type: '', text: '' });
    const label = userRow.email || userRow.username || `User ${userRow.id}`;

    try {
      await axios.delete(`/api/admin/users/${userRow.id}/hard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setUsers((prev) => prev.filter((item) => item.id !== userRow.id));
      setMessage({ type: 'success', text: `Deleted ${label}` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete user.' });
    }
  };

  if (!isAdmin) {
    return (
      <Card className="admin-users-access-card">
        <h2>Admin access required.</h2>
      </Card>
    );
  }

  const columns = [
    {
      title: 'User',
      dataIndex: 'full_name',
      render: (_, userRow) => (
        <div>
          <strong>{userRow.full_name || userRow.username || 'Unknown'}</strong>
          <div className="admin-users-email">{userRow.email || '-'}</div>
        </div>
      ),
    },
    {
      title: 'Completed',
      dataIndex: 'completed_modules_count',
      render: (_, userRow) => (
        <div>
          <strong>{userRow.completed_modules_count || 0}</strong>
          <div className="admin-users-email">
            {(userRow.completed_module_ids || []).slice(0, 2).join(', ') || '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      render: (_, userRow) => (
        <Select
          value={userRow.role}
          onChange={(value) => handleUserChange(userRow.id, 'role', value)}
          disabled={!canEditAdminRoles && (userRow.role === 'org_admin' || userRow.role === 'platform_admin')}
          options={isPlatformAdmin ? roleOptions : orgAdminRoleOptions}
          style={{ minWidth: 150 }}
        />
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      render: (_, userRow) => (
        <Switch
          checked={userRow.is_active}
          checkedChildren="Active"
          unCheckedChildren="Off"
          onChange={(checked) => handleUserChange(userRow.id, 'is_active', checked)}
        />
      ),
    },
    {
      title: 'Verified',
      dataIndex: 'is_verified',
      render: (_, userRow) => (
        <Switch
          checked={userRow.is_verified}
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={(checked) => handleUserChange(userRow.id, 'is_verified', checked)}
        />
      ),
    },
    {
      title: 'Organization',
      dataIndex: 'organization_id',
      render: (_, userRow) => (
        isPlatformAdmin ? (
          <Select
            value={userRow.organization_id || ''}
            onChange={(value) => handleUserChange(userRow.id, 'organization_id', value ? Number(value) : null)}
            options={[
              { value: '', label: '-' },
              ...organizations.map((org) => ({ value: org.id, label: `${org.name} (#${org.id})` })),
            ]}
            style={{ minWidth: 180 }}
          />
        ) : (
          <div className="admin-users-org">
            {orgNameById.get(userRow.organization_id) || (userRow.organization_id ? `Org #${userRow.organization_id}` : '-')}
          </div>
        )
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, userRow) => {
        const label = userRow.email || userRow.username || `User ${userRow.id}`;
        return (
          <Space wrap>
            <Button type="primary" icon={<Save size={16} />} onClick={() => saveUser(userRow)}>
              Save
            </Button>
            <Popconfirm
              title={`Deactivate ${label}?`}
              description="This will disable login for this user."
              okText="Deactivate"
              okButtonProps={{ danger: true }}
              onConfirm={() => deactivateUser(userRow)}
            >
              <Button danger icon={<Trash2 size={16} />}>Deactivate</Button>
            </Popconfirm>
            {isPlatformAdmin && (
              <Popconfirm
                title={`Permanently delete ${label}?`}
                description="This cannot be undone."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => hardDeleteUser(userRow)}
              >
                <Button danger icon={<Trash2 size={16} />}>Delete</Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="admin-users">
      <Card className="admin-users-toolbar-card">
        <div className="admin-users-card-title">
          <Users size={18} />
          <div>
            <strong>User Management</strong>
            <span>View and manage all users in the system.</span>
          </div>
        </div>
        <Form className="admin-users-filter-form" layout="inline" onFinish={() => fetchUsers(query)}>
          <Form.Item label="Search">
            <Input.Search
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onSearch={(value) => fetchUsers(value)}
              placeholder="Search by name or email"
              enterButton="Search"
              loading={loading}
              allowClear
            />
          </Form.Item>
        </Form>
      </Card>

      {message.text && (
        <Alert
          className="admin-users-alert"
          type={message.type === 'success' ? 'success' : 'error'}
          showIcon
          message={message.text}
        />
      )}

      <Table
        className="admin-users-table"
        rowKey="id"
        columns={columns}
        dataSource={users}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ x: 1100 }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No users found." /> }}
      />
    </div>
  );
};

export default AdminUsers;
